import { useCallback, useEffect, useState, useRef } from "react"
import {
  PracticeAffiliate,
  PracticeInviteWithContext,
  selectPracticeInviteStatusDisplayId,
  PracticeInviteStatusDisplayId,
} from "../../domain/Practice/schema"
import { apiService } from "../../features/shared/apiService"
import { DataStatus } from "../../domain/enums"
import { Provider, ProviderStatusDisplayId } from "../../domain/Provider/schema"
import { PracticeRoleEnum } from "../../domain/Account/userSlice"
import {
  autoSelectAffiliatedOrInvitePracticeId,
  AutoSelectedPracticeIdResult,
  getOnboardingFlowIdFromState,
  isUserOnboarded,
  userIsProviderSeeingPatients,
} from "../../domain/Practices/helpers"
import { PartialPractice } from "../../domain/Practices/schema"
import appInsightsLogger from "../../features/logging/appInsightsLogger"
import { useFeatureFlagEnabled } from "posthog-js/react"
import { FeatureFlag } from "../../domain/enums/featureFlags"

const {
  LOADING: LOADING_STATUS,
  LOADED: LOADED_STATUS,
  ERROR: ERRORS_STATUS,
} = DataStatus

type usePracticeInvitesByUserIdType = {
  usePracticeInvitesByUserIdDataStatus: DataStatus
  practiceInvites: PracticeInviteWithContext[]
  refetchPracticeInvites: () => Promise<void>
}
export const fetchCompletedUserSteps = async (userId: string) => {
  const userAccount = await apiService.account.get.USER_BY_ID({ userId })
  return userAccount.completedOnboardingSteps.$values
}

export const fetchPartialPracticesByUserId = async (userId: string) => {
  try {
    const practiceAffiliates =
      await apiService.practice.get.PRACTICE_AFFILIATES_BY_USER_ID({
        userId,
      })
    const practiceIds = practiceAffiliates.map((p) => p.practiceId)
    return await apiService.practice.get.PARTIAL_PRACTICES_BY_IDS(practiceIds)
  } catch {
    return new Promise<PartialPractice[]>((resolve) => {
      resolve([]) // Return an empty array if the API call fails
    })
  }
}

// v2: invites live nested on PracticeAffiliate. Flatten each affiliate's invites and
// stitch on practiceId/providerId from the parent affiliate.
export const flattenAffiliateInvites = (
  affiliates: PracticeAffiliate[],
): PracticeInviteWithContext[] =>
  affiliates.flatMap((affiliate) =>
    (affiliate.practiceInvites ?? []).map((invite) => ({
      ...invite,
      practiceId: affiliate.practiceId,
      providerId: affiliate.providerId,
    })),
  )

// Invites the user still has to action. The service-side statuses 'invited' and 'pending'
// both map to the single client-facing 'sent' status, so filter on the mapped value rather
// than on a raw service status.
export const filterToPendingInvites = (
  practiceInvites: PracticeInviteWithContext[],
): PracticeInviteWithContext[] =>
  practiceInvites.filter(
    (invite) =>
      selectPracticeInviteStatusDisplayId(invite.inviteStatusDisplayId) ===
      PracticeInviteStatusDisplayId.SENT,
  )

export const extendPracticeInvites = (
  practiceInvites: PracticeInviteWithContext[],
  partialPractices: PartialPractice[],
): PracticeInviteWithContext[] => {
  return practiceInvites
    .map((associatedInvite) => {
      const associatedPractice = partialPractices.find(
        (p) => p.id === associatedInvite.practiceId,
      )
      if (!associatedPractice) {
        return null
      }

      return {
        ...associatedInvite,
        practiceStatus: associatedPractice.status,
        isInvitationForProviderRole:
          associatedInvite.inviteRoleDisplayIds.includes(
            PracticeRoleEnum.PROVIDER,
          ),
        practiceHasConditionalReqs:
          associatedPractice.locations.length > 1 ||
          !associatedInvite.practiceProvidesLiabilityInsurance,
      } as PracticeInviteWithContext
    })
    .filter((e) => !!e) as PracticeInviteWithContext[]
}

export const getIsSeeingPatients = (
  provider: Provider | null,
  autoSelectedAffiliatedOrInvitePracticeId:
    | AutoSelectedPracticeIdResult
    | undefined,
  extendedPracticeInvites: PracticeInviteWithContext[],
) => {
  if (!provider || !autoSelectedAffiliatedOrInvitePracticeId) {
    return false
  }

  return userIsProviderSeeingPatients(
    provider,
    autoSelectedAffiliatedOrInvitePracticeId,
    extendedPracticeInvites,
  )
}

const providerByUserIdCache: Record<string, Provider | null> = {}

const tryAndFetchProviderByUserId = async (userId: string) => {
  if (userId in providerByUserIdCache) {
    return providerByUserIdCache[userId]
  }
  let provider = null
  try {
    provider = await apiService.provider.get.PROVIDER_BY_USER_ID({
      userId,
    })
  } catch {}
  providerByUserIdCache[userId] = provider
  return provider
}

const fetchPartialPracticesByPracticeIds = async (practiceIds: string[]) => {
  try {
    return apiService.practice.get.PARTIAL_PRACTICES_BY_IDS(practiceIds)
  } catch {
    appInsightsLogger.traceError(
      new Error(
        `Failed to fetch partial practices for practiceIds: ${practiceIds.join(",")}`,
      ),
    )
    return []
  }
}

const getIsUserOnboarded = async (
  userId: string,
  practiceInvites: PracticeInviteWithContext[],
  affiliatedPracticeIds: string[],
) => {
  if (!userId) {
    return
  }

  const [partialPractices, provider, completedUserOnboardingSteps] =
    await Promise.all([
      fetchPartialPracticesByPracticeIds(affiliatedPracticeIds),
      tryAndFetchProviderByUserId(userId),
      fetchCompletedUserSteps(userId),
    ])
  const extendedPracticeInvites = extendPracticeInvites(
    practiceInvites,
    partialPractices,
  )
  const autoSelectedPracticeIdResult = autoSelectAffiliatedOrInvitePracticeId(
    partialPractices,
    extendedPracticeInvites,
  )
  const isSeeingPatients = getIsSeeingPatients(
    provider,
    autoSelectedPracticeIdResult,
    extendedPracticeInvites,
  )
  const flowId = getOnboardingFlowIdFromState({
    partialPractices,
    provider,
    detachedProviders: [],
    practiceInvites: extendedPracticeInvites,
    autoSelectedPracticeIdResult,
    isSeeingPatients,
  })
  const affiliatedPractice = partialPractices.find(
    (practice: PartialPractice) =>
      practice.id === autoSelectedPracticeIdResult?.practiceId,
  )
  const userOnboarded = isUserOnboarded(
    flowId,
    completedUserOnboardingSteps,
    affiliatedPractice,
    provider,
    false,
  )
  return userOnboarded
}

// Module-level cache persists across component mounts so remounts caused by
// parent loading cycles don't re-fire the API. fetchingPromises deduplicates
// concurrent fetches for the same key (e.g. React 18 StrictMode double-invoke,
// or brief unmount/remount while a fetch is in-flight) so that the waiting
// component can still resolve its own dataStatus when the fetch completes.
const inviteCache: Record<string, PracticeInviteWithContext[]> = {}
const fetchingPromises: Record<string, Promise<void> | undefined> = {}

const usePracticeInvitesByUserId = (
  userId: string | null,
  isActingUserAffiliateRelations: boolean,
): usePracticeInvitesByUserIdType => {
  const isProviderOrphanSupport16529Enabled = useFeatureFlagEnabled(
    FeatureFlag.IsProviderOrphanSupport16529Enabled,
  )
  const [practiceInvites, setPracticeInvites] = useState<
    PracticeInviteWithContext[]
  >([])
  const [dataStatus, setDataStatus] = useState<DataStatus>(LOADING_STATUS)

  const currentUserIdRef = useRef<string | null>(userId)
  currentUserIdRef.current = userId

  const fetchPracticeInvitesByUserId = useCallback(async () => {
    if (!userId) {
      setDataStatus(LOADED_STATUS)
      return () => {}
    }

    const requestUserId = userId
    const cacheKey = `${userId}-${isActingUserAffiliateRelations}`
    if (inviteCache[cacheKey]) {
      setPracticeInvites(inviteCache[cacheKey])
      setDataStatus(LOADED_STATUS)
      return
    }

    if (fetchingPromises[cacheKey]) {
      try {
        await fetchingPromises[cacheKey]
        if (inviteCache[cacheKey]) {
          setPracticeInvites(inviteCache[cacheKey])
        }
        setDataStatus(LOADED_STATUS)
      } catch {
        setDataStatus(ERRORS_STATUS)
      }
      return
    }

    setPracticeInvites([])

    let resolveFetch: () => void = () => {}
    let rejectFetch: (e: unknown) => void = () => {}
    fetchingPromises[cacheKey] = new Promise<void>((res, rej) => {
      resolveFetch = res
      rejectFetch = rej
    })

    try {
      // v2: invites are nested on affiliates; flatten and stitch parent context.
      const affiliates =
        await apiService.practice.get.PRACTICE_AFFILIATES_BY_USER_ID({
          userId,
          includeDeactivated: true, // this allows us to fetch invites that have not been accepted yet
        })
      const flattenedInvites = flattenAffiliateInvites(affiliates)
      let newPracticeInvites = filterToPendingInvites(flattenedInvites)
      if (
        isActingUserAffiliateRelations &&
        newPracticeInvites.some((invite) =>
          invite.inviteRoleDisplayIds.includes(PracticeRoleEnum.PROVIDER),
        )
      ) {
        // check if user is provider and if their status is pending or pending-activation
        const provider = await tryAndFetchProviderByUserId(userId)
        const statusesToFilter = [
          "pending",
          "pending-activation",
        ] as ProviderStatusDisplayId[]
        if (!isProviderOrphanSupport16529Enabled) {
          statusesToFilter.push("orphaned" as ProviderStatusDisplayId)
        }

        if (
          !provider ||
          (provider &&
            statusesToFilter.includes(provider.providerStatusDisplayId))
        ) {
          /**
           * AR users cannot have access to pending/pending-activation providers.
           * This is to ensure invitations are not actioned out of order within the onboarding workflow.
           * (https://dev.azure.com/HACloud/VEAP/_workitems/edit/7566)
           */
          newPracticeInvites = newPracticeInvites.filter(
            (invite) =>
              !invite.inviteRoleDisplayIds.includes(PracticeRoleEnum.PROVIDER),
          )
        }
      }

      if (isActingUserAffiliateRelations) {
        const affiliatedPracticeIds = affiliates.map((a) => a.practiceId)
        const isUserOnboarded = await getIsUserOnboarded(
          userId,
          flattenedInvites,
          affiliatedPracticeIds,
        )
        if (!isUserOnboarded) {
          if (requestUserId === currentUserIdRef.current) {
            inviteCache[cacheKey] = []
            setDataStatus(LOADED_STATUS)
          }
          resolveFetch()
          return
        }
      }
      if (requestUserId === currentUserIdRef.current) {
        inviteCache[cacheKey] = newPracticeInvites
        setPracticeInvites(newPracticeInvites)
        setDataStatus(LOADED_STATUS)
      }
      resolveFetch()
    } catch (error) {
      if (requestUserId === currentUserIdRef.current) {
        appInsightsLogger.traceError(error)
        setDataStatus(ERRORS_STATUS)
      }
      rejectFetch(error)
    } finally {
      delete fetchingPromises[cacheKey]
    }
  }, [
    isActingUserAffiliateRelations,
    userId,
    isProviderOrphanSupport16529Enabled,
  ])

  const refetchPracticeInvites = useCallback(async () => {
    const cacheKey = `${userId}-${isActingUserAffiliateRelations}`
    if (fetchingPromises[cacheKey]) return

    if (userId) {
      delete inviteCache[cacheKey]
      delete providerByUserIdCache[userId]
    }

    await fetchPracticeInvitesByUserId()
  }, [fetchPracticeInvitesByUserId, isActingUserAffiliateRelations, userId])

  useEffect(() => {
    fetchPracticeInvitesByUserId()
  }, [fetchPracticeInvitesByUserId])

  return {
    practiceInvites,
    usePracticeInvitesByUserIdDataStatus: dataStatus,
    refetchPracticeInvites,
  }
}

export default usePracticeInvitesByUserId
